import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Asset, InstrumentType } from '@zero/shared/util-models';
import { InstrumentDto } from '@zero/shared/util-zero-api-client';
import { lowTradingThreshold, minimumOrderValue } from 'constants/shared-constants';
import { environment } from 'environments/environment';
import { catchError, map, Observable, of } from 'rxjs';
import { InstrumentSearchParams, InstrumentSearchResponse } from '../../entities/instrument-search.entity';

@Injectable({
  providedIn: 'root',
})
export class InstrumentSearchService {
  readonly #http = inject(HttpClient);
  readonly #endpoint = environment.apihost + environment.instrumentSearch;

  #getMinimumOrderValue(instrumentMinimumOrderValue: number): number {
    return instrumentMinimumOrderValue || minimumOrderValue;
  }

  public searchByQuery(query?: string, params?: InstrumentSearchParams) {
    const httpParams = { q: query ?? '', ...(params ? this.#buildParams(params) : {}) };
    return this.#http.get<InstrumentSearchResponse>(this.#endpoint, {
      params: httpParams,
    });
  }

  public searchByIsin(isin?: string, params?: InstrumentSearchParams) {
    const httpParams = { i: isin ?? '', ...(params ? this.#buildParams(params) : {}) };
    return this.#http.get<InstrumentSearchResponse>(this.#endpoint, {
      params: httpParams,
    });
  }

  #buildParams(params: InstrumentSearchParams) {
    let newParams = { ...params } as any;
    if (params.type) {
      newParams = { ...params, type: params?.type?.join(',') };
    }
    return newParams;
  }

  public getAssetByIsin(isin: string): Observable<Asset> {
    return this.#http.get(`${environment.apihost + environment.instrumentServiceUrl}/${isin}`).pipe(
      map((instrument: InstrumentDto) => {
        if (!instrument?.isin) {
          return {} as Asset;
        }

        const {
          type,
          leverage,
          complex,
          isin,
          wkn,
          longName,
          tradingHoursStopTime,
          tradingHoursStartTime,
          marketName,
          endfaellig,
          basisinformationen,
          knockedOutFlag,
          jahresbericht,
          verkaufsprospekt,
          halbjahresbericht,
          emittentShortCode,
          sparplan,
          underlyingIsin,
          underlying,
          priceScale,
          shortName,
          iconUrl,
          historicQuotes,
          quoteCurrencyCode,
          fractionalTrading,
          ranking,
          sentiment,
          isOTC,
          tradingFees,
        } = instrument;

        return {
          instrumentType: type as InstrumentType,
          leverage,
          complex,
          isin,
          wkn,
          name: longName,
          marketName,
          baseInfoLink: basisinformationen,
          salesbrochure: verkaufsprospekt,
          semiAnnualReport: halbjahresbericht,
          annualReport: jahresbericht,
          knockedOutFlag,
          endfaellig,
          tradingHoursStartTime,
          tradingHoursStopTime,
          emittentShortCode,
          sparplan,
          minimumOrderValue: this.#getMinimumOrderValue(instrument.minimumOrderValue),
          lowTradingThreshold: instrument.lowTradingThreshold ?? lowTradingThreshold,
          underlyingIsin,
          underlyingName: underlying,
          priceScale,
          longName,
          shortName,
          iconUrl,
          historicQuotes,
          quoteCurrencyCode,
          fractionalTrading,
          ranking,
          sentiment,
          isOTC,
          tradingFees,
        };
      }),
      catchError((e) => {
        console.error(e);
        return of(undefined);
      }),
    );
  }
}
